/**
 * MenuItem - An atom component for individual menu items.
 *
 * A clickable menu item with optional icon and text.
 * Used within Menu components.
 */

import { type ReactNode } from 'react';
import { type LucideIcon } from 'lucide-react';
import { cn } from '@lean-agility/design-system/react';

export interface MenuItemProps {
  /**
   * Optional icon to display before the text.
   */
  icon?: LucideIcon;
  /**
   * The menu item content (text).
   */
  children: ReactNode;
  /**
   * Callback invoked when the menu item is clicked.
   */
  onClick?: () => void;
  /**
   * Whether the menu item is disabled.
   */
  disabled?: boolean;
  /**
   * Additional CSS classes.
   */
  className?: string;
}

export function MenuItem({ icon: Icon, children, onClick, disabled, className }: MenuItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm',
        `
          text-text-primary
          dark:text-text-primary
        `,
        `
          hover:bg-bg-base
          dark:hover:bg-bg-subtle
        `,
        'transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
    >
      {Icon && <Icon className="size-4 shrink-0" />}
      <span>{children}</span>
    </button>
  );
}
